import React, { useState } from 'react';
import { Container, Row, Col, Card, Table, Button, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import useCartStore from '../store/cartStore';
import useAuthStore from '../store/authStore';
import { ordersAPI } from '../services/api';
import { toast } from 'react-toastify';

const Checkout = () => {
  const { items, getTotal, getCartItems, clearCart } = useCartStore();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);

  const handlePayment = async () => {
    setIsProcessing(true);

    try {
      // Create order
      const orderData = {
        orderItems: getCartItems(),
        totalAmount: getTotal(),
      };

      const response = await ordersAPI.create(orderData);
      const { razorpayOrderId, amount, keyId, orderId } = response.data;

      // Razorpay options
      const options = {
        key: keyId,
        amount: amount * 100,
        currency: 'INR',
        name: 'IT World Store',
        description: 'Software License Purchase',
        order_id: razorpayOrderId,
        handler: async function (response) {
          try {
            // Verify payment
            await ordersAPI.verify({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              orderId: orderId,
            });

            toast.success('Payment successful! Check your email for license keys.');
            clearCart();
            navigate(`/orders/${orderId}`);
          } catch (error) {
            toast.error('Payment verification failed. Please contact support.');
            setIsProcessing(false);
          }
        },
        prefill: {
          name: user.name,
          email: user.email,
          contact: user.phone || '',
        },
        theme: {
          color: '#0d6efd',
        },
        modal: {
          ondismiss: function () {
            setIsProcessing(false);
            toast.info('Payment cancelled');
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create order');
      setIsProcessing(false);
    }
  };

  if (items.length === 0) {
    return (
      <Container className="my-5 text-center">
        <Alert variant="warning">
          <h4>Your cart is empty</h4>
          <p>Add some products before checking out</p>
          <Button variant="primary" onClick={() => navigate('/products')}>
            Browse Products
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="my-5">
      <h1 className="mb-4">Checkout</h1>

      <Row>
        <Col lg={8}>
          <Card className="mb-4">
            <Card.Header>
              <h5>Order Items</h5>
            </Card.Header>
            <Card.Body>
              <Table responsive>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Quantity</th>
                    <th>Price</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const cartItem = getCartItems().find(ci => ci.productId === item._id);
                    return (
                      <tr key={item._id}>
                        <td>{item.name}</td>
                        <td>{item.quantity}</td>
                        <td>₹{cartItem.pricePerUnit}</td>
                        <td>₹{cartItem.pricePerUnit * item.quantity}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Header>
              <h5>Customer Information</h5>
            </Card.Header>
            <Card.Body>
              <p><strong>Name:</strong> {user.name}</p>
              <p><strong>Email:</strong> {user.email}</p>
              <p><strong>Phone:</strong> {user.phone || 'Not provided'}</p>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card>
            <Card.Header>
              <h5>Order Summary</h5>
            </Card.Header>
            <Card.Body>
              <div className="d-flex justify-content-between mb-2">
                <span>Subtotal:</span>
                <strong>₹{getTotal()}</strong>
              </div>
              <div className="d-flex justify-content-between mb-3">
                <span>Total Items:</span>
                <span>{items.reduce((sum, item) => sum + item.quantity, 0)}</span>
              </div>
              <hr />
              <div className="d-flex justify-content-between mb-3">
                <h5>Total Amount:</h5>
                <h5 className="text-primary">₹{getTotal()}</h5>
              </div>

              <div className="d-grid">
                <Button
                  variant="success"
                  size="lg"
                  onClick={handlePayment}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Processing...
                    </>
                  ) : (
                    'Pay with Razorpay'
                  )}
                </Button>
              </div>
            </Card.Body>
          </Card>

          <Alert variant="info" className="mt-3">
            <small>
              <strong>Note:</strong> Your license keys will be delivered instantly via email and WhatsApp after successful payment.
            </small>
          </Alert>
        </Col>
      </Row>
    </Container>
  );
};

export default Checkout;
